/**
 * fractalSmokeTouchPipeline — the smoke reacts to your finger.
 *
 * Based on fractalSmokePipeline.ts. The pointer acts as a "blower":
 * smoke is pushed away from the cursor (radial displacement of the
 * noise sample point). Click adds a turbulence impulse that
 * briefly thickens the smoke and adds new wisp detail.
 *
 * Pointer is sent in y-up canvas pixel space (the embed's forwarder
 * has already flipped y). The shader does the conversion to the
 * shader's p-space.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface FractalSmokeTouchPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  uPointer: WebGLUniformLocation | null;
  uPointerActive: WebGLUniformLocation | null;
  uClickPos: WebGLUniformLocation | null;
  uClickAge: WebGLUniformLocation | null;
  bands: Float32Array;
  smoothX: number;
  smoothY: number;
  _active: number;
  clicks: { x: number; y: number; t: number }[];
}

const MAX_CLICKS = 2;
const CLICK_LIFETIME = 1.4;

const VERT_SRC = `#version 300 es
precision highp float;
layout(location=0) in vec2 aPos; out vec2 vUV;
void main(){ vUV = aPos*0.5+0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

const FRAG_SRC = `#version 300 es
precision highp float; in vec2 vUV; out vec4 outColor;
uniform vec2 uResolution; uniform float uTime;
uniform vec3 uBass, uMid, uHigh;
// Pointer in canvas pixels (y-up, after the embed's y-flip).
uniform vec2 uPointer;
uniform float uPointerActive;
// Up to 2 active clicks.
uniform vec4 uClickPos;
uniform vec4 uClickAge;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fractalSmoke(vec2 p, float t) {
  float v = 0.0;
  float a = 0.5;
  float scale = 1.0;
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    float n = valueNoise(p * scale + vec2(t * 0.3 + fi, t * 0.2));
    v += a * n;
    scale *= 2.0;
    a *= 0.5 * (1.0 + n * 0.5);
  }
  return v;
}

// Click turbulence: when the user clicks, a localized burst of
// extra noise amplitude radiates from the click point. Reads in
// canvas pixels (y-up), returns 0..1.
float clickTurb(vec2 p, vec2 clickPx, float age) {
  if (clickPx.x < 0.0) return 0.0;
  vec2 cPos = (clickPx - 0.5 * uResolution) / uResolution.y;
  float d = length(p - cPos);
  // Turbulence expands then fades. Energy falls off as 1/r in 2D.
  float radius = age * 0.45;
  float band = exp(-pow((d - radius) * 14.0, 2.0));
  float fade = 1.0 / (1.0 + age * 1.5);
  return band * fade;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  // Pointer in shader-space. The forwarder already flipped y, so
  // this lands correctly: p.y up = pPos.y up.
  vec2 pPos = (uPointer - 0.5 * uResolution) / uResolution.y;
  vec2 fromPtr = p - pPos;
  float ptrD = length(fromPtr);
  vec2 ptrDir = ptrD > 1e-4 ? fromPtr / ptrD : vec2(0.0);

  // Blow smoke AWAY from the pointer: displace the noise sample
  // point in the direction away from the cursor. Strength falls
  // off with distance so far-away smoke isn't affected.
  float blowFalloff = 1.0 - smoothstep(0.0, 0.35, ptrD);
  float blowStrength = uPointerActive * blowFalloff * 0.4;
  // The displaced sample point: the closer to the pointer, the
  // more the sample is "pushed away", which visually moves the
  // smoke away from the cursor.
  vec2 sampleP = p + ptrDir * blowStrength;

  float r = length(p);
  vec3 bg = mix(vec3(0.10, 0.08, 0.10), vec3(0.02, 0.02, 0.04), smoothstep(0.0, 0.7, r));

  // Smoke rises from the bottom. Sample the displaced point so the
  // blow effect propagates through the smoke field.
  vec2 smokeP = vec2(sampleP.x, sampleP.y + uTime * 0.3);
  float smoke = fractalSmoke(smokeP * 3.0, uTime);

  // Click turbulence — extra amplitude in the noise near the click
  // wave. Two click slots.
  float turb = max(clickTurb(p, uClickPos.xy, uClickAge.x),
                   clickTurb(p, uClickPos.zw, uClickAge.y));
  // Bump the smoke by the turbulence. This adds new wisps wherever
  // the click wave passes.
  smoke += turb * 0.6 * (1.0 - smoke); // additive, fades in

  float smokeMask = smoothstep(0.5, -0.3, p.y);
  // The blow also slightly reduces the smoke mask near the pointer
  // (you've blown the smoke aside).
  smokeMask *= 1.0 - blowFalloff * uPointerActive * 0.5;
  float smokeIntensity = smoke * smokeMask;

  vec3 smokeColor = vec3(0.65, 0.60, 0.55);
  vec3 hotColor = vec3(0.95, 0.55, 0.25);
  vec3 finalColor = mix(smokeColor, hotColor, smoothstep(0.4, 0.7, smokeIntensity));

  vec3 col = mix(bg, finalColor, smokeIntensity * (0.6 + 0.4 * mid));

  // Heat shimmer.
  float shimmer = valueNoise(p * 8.0 + vec2(0.0, uTime * 2.0)) * 0.1;
  col += vec3(0.95, 0.6, 0.3) * shimmer * smokeMask;

  // Embers — slightly displaced by the pointer too, so they
  // scatter away from the cursor.
  for (int i = 0; i < 20; i++) {
    float fi = float(i);
    // Original ember position (per time + per ember seed).
    vec2 emberBase = vec2(
      sin(uTime * 0.5 + fi * 1.7) * 0.3,
      fract(uTime * 0.2 + hash21(vec2(fi, 7.0))) * 0.8 - 0.4
    );
    // Displace by pointer (embers get blown too).
    vec2 emberDir = emberBase - pPos;
    float eD = length(emberDir);
    vec2 eDir = eD > 1e-4 ? emberDir / eD : vec2(0.0);
    float eBlow = (1.0 - smoothstep(0.0, 0.25, eD)) * uPointerActive * 0.15;
    vec2 emberPos = emberBase + eDir * eBlow;
    float d = length(p - emberPos);
    col += vec3(1.0, 0.5, 0.2) * exp(-d * 200.0) * 0.7;
  }

  // A subtle "you can blow here" indicator: a soft ring around
  // the pointer when active. Sells the touch.
  float ring = 1.0 - smoothstep(0.0, 0.02, abs(ptrD - 0.04));
  col += vec3(0.7, 0.95, 1.0) * ring * uPointerActive * 0.4;

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);
}`;

export function computeFractalSmokeTouchBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeFractalSmokeTouchRealtimeBands(
  freqData: Uint8Array, out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) { sum += freqData[k]; count++; }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createFractalSmokeTouchPipeline(gl: WebGL2RenderingContext): FractalSmokeTouchPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution:    gl.getUniformLocation(program, "uResolution"),
    uTime:          gl.getUniformLocation(program, "uTime"),
    uBass:          gl.getUniformLocation(program, "uBass"),
    uMid:           gl.getUniformLocation(program, "uMid"),
    uHigh:          gl.getUniformLocation(program, "uHigh"),
    uPointer:       gl.getUniformLocation(program, "uPointer"),
    uPointerActive: gl.getUniformLocation(program, "uPointerActive"),
    uClickPos:      gl.getUniformLocation(program, "uClickPos"),
    uClickAge:      gl.getUniformLocation(program, "uClickAge"),
    bands: new Float32Array(9),
    smoothX: 0, smoothY: 0,
    _active: 0,
    clicks: [],
  };
}

export function drawFractalSmokeTouch(
  gl: WebGL2RenderingContext,
  pipe: FractalSmokeTouchPipeline,
  width: number, height: number, t: number,
): void {
  // Compute click uniforms from the ring buffer.
  const cp = [0, 0, 0, 0];
  const ca = [0, 0, 0, 0];
  const c0 = pipe.clicks[0];
  const c1 = pipe.clicks[1];
  if (c0) {
    const age = t - c0.t;
    if (age < CLICK_LIFETIME) {
      cp[0] = c0.x; cp[1] = c0.y; ca[0] = age;
    } else { cp[0] = -1; ca[0] = CLICK_LIFETIME; }
  } else { cp[0] = -1; ca[0] = CLICK_LIFETIME; }
  if (c1) {
    const age = t - c1.t;
    if (age < CLICK_LIFETIME) {
      cp[2] = c1.x; cp[3] = c1.y; ca[1] = age;
    } else { cp[2] = -1; ca[1] = CLICK_LIFETIME; }
  } else { cp[2] = -1; ca[1] = CLICK_LIFETIME; }

  gl.useProgram(pipe.program);
  gl.uniform2f(pipe.uResolution!, width, height);
  gl.uniform1f(pipe.uTime!, t);
  gl.uniform3f(pipe.uBass!, pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(pipe.uMid!, pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(pipe.uHigh!, pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.uniform2f(pipe.uPointer!, pipe.smoothX, pipe.smoothY);
  gl.uniform1f(pipe.uPointerActive!, pipe._active);
  gl.uniform4f(pipe.uClickPos!, cp[0], cp[1], cp[2], cp[3]);
  gl.uniform4f(pipe.uClickAge!, ca[0], ca[1], ca[2], ca[3]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
